<template>
  <NuxtLayout
    name="sticky"
    header-margin-bottom="mb-0"
    content-margin-top="mt-0"
    hide-footer
  >
    <template #sticky>
      <TContainer :mobile-full="true" :desktop-full="true">
        <div class="grid grid-cols-8 gap-2 lg:gap-1">
          <div class="col-span-8 lg:col-span-6">
            <CottageListSearch
              :model-value="locationName"
              @location-selected="handleLocationChange"
            />
          </div>
          <UButton
            size="xl"
            icon="i-heroicons-adjustments-horizontal-20-solid"
            color="neutral"
            variant="outline"
            class="col-span-4 lg:col-span-1"
            data-test="filters-button"
            @click="toggleFilters"
          >
            {{ t('views.cottage-list.results.buttons.filters') }}
          </UButton>
          <UButton
            size="xl"
            icon="i-heroicons-map-pin-20-solid"
            color="neutral"
            variant="outline"
            class="col-span-4 lg:col-span-1"
            data-test="map-button"
            @click="toggleMap"
          >
            {{ t('views.cottage-list.results.buttons.list') }}
          </UButton>
        </div>
      </TContainer>
    </template>

    <UModal
      v-model:open="showFilters"
      :fullscreen="isMobile"
      :ui="filtersModal"
    >
      <template #body>
        <CottageListFilters
          :current-location-name="locationName"
          :current-filters="currentFiltersData"
          @submit="handleFiltersSubmit"
          @close="toggleFilters"
        />
      </template>
    </UModal>

    <div
      class="relative"
      style="
        height: calc(100vh - 200px);
        min-height: 500px;
        width: 100vw;
        max-width: 100%;
      "
      :style="{
        height: isDesktop ? 'calc(100vh - 180px)' : 'calc(100vh - 200px)',
      }"
    >
      <TOverlay :show="mapIsLoading" :fixed="false" />
      <ClientOnly>
        <CottageListMap
          ref="cottageListMapRef"
          :destination="destinationData"
        />
      </ClientOnly>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import { useMenuBreadcrumbStore } from '~~/modules/menu/stores/useMenuBreadcrumbStore'
import { useCottageListFilters } from '~~/modules/views/cottage-list/composables/useCottageListFilters'
import { filtersModal } from '~~/modules/design-system/utils/nuxt-ui-config/components/modal'
import CottageListMap from '~~/modules/views/cottage-list/components/CottageListMap.client.vue'
import { serverFetch as fetchDestination } from '~~/modules/api/rurality/v3/destinations'
import type { schemaType as DestinationSchemaType } from '~~/shared/api/rurality/v3/destinations/schema'
import { COTTAGE_LIST_MAP_ROUTE_NAME } from '~~/modules/views/cottage-list/constants'

definePageMeta({
  name: COTTAGE_LIST_MAP_ROUTE_NAME,
})

const { setHeadInfo } = useHeadInfoComposable()
const { isMobile, isDesktop } = useDevice()
const route = useRoute()
const breadCrumbMenuStore = useMenuBreadcrumbStore()

const { handleLocationChange, updateFilters, getCurrentFilters } =
  useCottageListFilters()

breadCrumbMenuStore.$state.breadCrumbMenu = []

const currentFilters = getCurrentFilters()

const locationName = ref(t('views.cottage-list.config.default-country'))
const destinationData = ref<DestinationSchemaType['destination'] | null>(null)

if (currentFilters.l) {
  const destinationResponse = await fetchDestination({
    destination: currentFilters.l,
  })
  if (destinationResponse?.success?.data.destination.name) {
    const destination = destinationResponse.success.data.destination
    locationName.value = destination.name
    destinationData.value = destination
  }
}

const showFilters = ref(false)
const cottageListMapRef = ref<InstanceType<typeof CottageListMap> | null>(null)

const currentFiltersData = computed(() => getCurrentFilters())

const mapIsLoading = computed(() => cottageListMapRef.value?.isLoading ?? true)

function handleFiltersSubmit(filters: any) {
  showFilters.value = false
  updateFilters(filters, 1)
}

function toggleFilters() {
  showFilters.value = !showFilters.value
}

function toggleMap() {
  const query = { ...route.query }
  navigateTo(
    {
      path: routes().cottageList(),
      query,
    },
    {
      external: true,
    },
  )
}

setHeadInfo({
  title: t('views.cottage-list.map.head.title', {
    location: locationName.value,
  }),
  description: t('views.cottage-list.map.head.description', {
    location: locationName.value,
  }),
  robotsVisible: true,
})
</script>
